import { dedent } from 'ts-dedent'
import { v4 as uuid4 } from 'uuid'
import {
  GetOutputSchemaParams,
  Language,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class CrossJoin extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.crossjoin',
        group: 'Joins',
        title: 'Cross Join',
        description: `
      This component performs a cross join.
      `,
        starred: true
      },
      [
        {
          name: 'maintable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Main table'
        },
        {
          name: 'secondarytable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Secondary table'
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const outputTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )
    const preparedParams = this._prepareParameters(params)
    let select = ''
    if (connection.type === Provider.DatabricksRest) {
      select = `SELECT /*+ REPARTITION(40) */
          ''' + select_clause_str + '''
        FROM
        ${connection.quoteName(preparedParams.maintable as string)} AS _main,
        ${connection.quoteName(
          preparedParams.secondarytable as string
        )} AS _joined`
    } else {
      select = `SELECT
    _main.*, %s
    FROM
    ${connection.quoteName(preparedParams.maintable as string)} AS _main,
    ${connection.quoteName(preparedParams.secondarytable as string)} AS _joined`
    }
    const createTable = connection.getCreateTempTableQuery(
      outputTablename,
      select,
      codeType,
      null
    )
    let query = ''
    const storedProc = codeType === WorkflowCodeType.StoredProcedure
    switch (connection.type) {
      case Provider.BigQuery: {
        const tokens = (preparedParams.secondarytable as string)?.split('.')
        const datasetname = storedProc
          ? connection.tempLocation
          : `${tokens[0]}.${tokens[1]}`
        const tablename = storedProc
          ? `table_${uuid4().replace(/-/g, '_')}`
          : tokens[2]
        const storedProcTable = connection.quoteName(
          `${connection.tempLocation}.${tablename}`
        )
        query = dedent`DECLARE alias STRING;
              ${
                storedProc
                  ? dedent`
                      CREATE TABLE ${storedProcTable} AS
                      SELECT * FROM ${connection.quoteName(
                        preparedParams.secondarytable as string
                      )}
                      WHERE 1=0;`
                  : ''
              }
              EXECUTE IMMEDIATE
              '''
                with __alias AS(
                  SELECT CONCAT(
                    '_joined.', column_name, ' AS ', column_name, '_joined'
                  ) col_alias
                  FROM \`${datasetname}\`.INFORMATION_SCHEMA.COLUMNS
                WHERE table_name = '${tablename}'
                )
                SELECT STRING_AGG(col_alias, ', ')
                FROM __alias
              '''
              INTO alias;
              ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
              ${dedent(
                connection.executeDynamicSQL({
                  sql: createTable,
                  addQuotes: true,
                  replacements: [["'%s'", 'alias']]
                })
              )}`
        break
      }
      case Provider.Snowflake: {
        let datasetname, schemaname, tablename, storedProcTable
        if (storedProc) {
          ;[datasetname, schemaname] = connection.tempLocation.split('.')
          tablename = `table_${uuid4().replace(/-/g, '_')}`
          storedProcTable = connection.quoteName(
            `${connection.tempLocation}.${tablename}`
          )
        } else {
          ;[datasetname, schemaname, tablename] = (
            preparedParams.secondarytable as string
          ).split('.')
        }
        query = dedent`DECLARE
                  alias STRING;
                BEGIN
                  ${
                    storedProc
                      ? dedent`
                          CREATE TABLE ${storedProcTable} AS
                          SELECT * FROM ${connection.quoteName(
                            preparedParams.secondarytable as string
                          )}
                          WHERE 1=0;`
                      : ''
                  }
                  LET res resultset := (EXECUTE IMMEDIATE '
                    with __alias AS(
                      SELECT DISTINCT CONCAT(
                        ''_joined.'', column_name, '' AS '', column_name, ''_joined''
                      ) col_alias
                      FROM ${datasetname}.INFORMATION_SCHEMA.COLUMNS
                      WHERE table_name = ''${tablename.toUpperCase()}''
                    )
                    SELECT LISTAGG(col_alias, '', '') aliases
                    FROM __alias
                  ');
                  LET cur_res CURSOR FOR res;
                  OPEN cur_res;
                  FETCH cur_res INTO alias;
                  ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
                  EXECUTE IMMEDIATE
                    REPLACE(
                      '${dedent(createTable)}',
                      '%s',
                      alias
                    );
                END;`
        break
      }
      case Provider.Postgres: {
        let datasetname, schemaname, tablename, storedProcTable
        if (storedProc) {
          ;[datasetname, schemaname] = connection.tempLocation.split('.')
          tablename = `table_${uuid4().replace(/-/g, '_')}`
          storedProcTable = connection.quoteName(
            `${connection.tempLocation}.${tablename}`
          )
        } else {
          ;[datasetname, schemaname, tablename] = (
            preparedParams.secondarytable as string
          ).split('.')
        }
        query = dedent`DECLARE
                alias TEXT;
              BEGIN
                ${
                  storedProc
                    ? dedent`
                        CREATE TABLE ${storedProcTable} AS
                        SELECT * FROM ${connection.quoteName(
                          preparedParams.secondarytable as string
                        )}
                        WHERE 1=0;`
                    : ''
                }
                EXECUTE '
                  with __alias AS(
                    SELECT DISTINCT CONCAT(
                      ''_joined.'', column_name, '' AS '', column_name, ''_joined''
                    ) col_alias
                    FROM "${datasetname}".INFORMATION_SCHEMA.COLUMNS
                    WHERE table_name ilike ''${tablename}''
                  )
                  SELECT STRING_AGG(col_alias, '', '') aliases
                  FROM __alias
                '
                INTO alias;
                ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
                EXECUTE
                  REPLACE(
                    '${dedent(createTable)}',
                    '%s',
                    alias
                  );
              END;`
        break
      }
      case Provider.Redshift: {
        const tempselect = connection.getTempSelectQuery(select, dryRun)
        const temptable = `${outputTablename}_temp`
        let datasetname, schemaname, tablename, storedProcTable
        if (storedProc) {
          ;[datasetname, schemaname] = connection.tempLocation.split('.')
          tablename = `table_${uuid4().replace(/-/g, '_')}`
          storedProcTable = connection.quoteName(`${schemaname}.${tablename}`)
        } else {
          ;[datasetname, schemaname, tablename] = (
            preparedParams.secondarytable as string
          ).split('.')
        }
        query = dedent`
            DECLARE
              _alias VARCHAR(MAX);
            BEGIN
              ${
                storedProc
                  ? dedent`
                      ${connection.getDropTableQuery(storedProcTable)}
                      CREATE TABLE ${storedProcTable} AS
                      SELECT * FROM ${connection.quoteName(
                        preparedParams.secondarytable as string
                      )}
                      WHERE 1=0;`
                  : ''
              }
              ${connection.getDropTableQuery(temptable)}
              EXECUTE '
                CREATE TABLE ${temptable} AS
                  SELECT DISTINCT
                    ''_joined.'' || column_name || '' AS '' || column_name || ''_joined''
                  AS col_alias
                  FROM svv_columns
                  WHERE table_name ILIKE ''${tablename}'' AND
                    table_schema ILIKE ''${schemaname}'' AND
                    table_catalog ILIKE ''${datasetname}''
              ';
              EXECUTE '
                SELECT LISTAGG(col_alias, '', '') aliases
                FROM ${temptable};
              ' INTO _alias;
              ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
              DROP TABLE IF EXISTS ${outputTablename} CASCADE;
              EXECUTE
                REPLACE(
                  'CREATE${
                    storedProc ? ' TEMPORARY' : ''
                  } TABLE ${outputTablename} AS
                  ${dedent(tempselect)}',
                  '%s',
                  _alias
                );
              DROP TABLE ${temptable};
            END;`
        break
      }
      case Provider.DatabricksRest: {
        query = `
from pyspark.sql.functions import col

l_df = spark.sql("select * from ${connection.quoteName(
          preparedParams.maintable as string
        )}")
r_df = spark.sql("select * from ${connection.quoteName(
          preparedParams.secondarytable as string
        )}")

# Get the list of columns from both DataFrames
l_columns = l_df.columns
r_columns = r_df.columns

# Specify the special columns
special_columns = ['__carto_xmin', '__carto_ymin', '__carto_xmax', '__carto_ymax']

# Initialize an empty list to build the SELECT clause
select_clause = []

# Handle left DataFrame columns
for col in l_columns:
    if col in special_columns:
        select_clause.append(f"_main.{col} AS {col}")
    else:
        select_clause.append(f"_main.{col} AS l_{col}")

# Handle right DataFrame columns
for col in r_columns:
    if col in special_columns:
        if col not in l_columns:
            select_clause.append(f"_joined.{col} AS {col}")
    else:
        select_clause.append(f"_joined.{col} AS r_{col}")

# Combine the SELECT statement
select_clause_str = ", ".join(select_clause)

${(connection as DBricksConnection).wrapSQLCodeAsPythonCall(createTable)}`
        break
      }
      default:
        return null
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns }: GetOutputSchemaParams): TableSchema {
    const schema = columns.maintable.map((column) => {
      return {
        name: `${column.name}`,
        type: column.type
      }
    })
    columns.secondarytable.forEach((column) => {
      schema.push({
        name: `${column.name}_joined`,
        type: column.type
      })
    })
    return schema
  }
}

export { CrossJoin }
